import { useEffect, useRef, useState } from 'react';

import {
  useGetMfaStatusQuery,
  useSetupMfaMutation,
  useEnableMfaMutation,
  useDisableMfaMutation,
  useRegenerateBackupCodesMutation,
} from '../../../../services/user';
import { trackEvent } from '../../../../helpers/analytics';

// View states for the MFA card.
export const MFA_VIEW = {
  IDLE: 'idle', // showing current status (enabled / disabled)
  SETUP: 'setup', // QR code + secret + code input
  BACKUP_CODES: 'backupCodes', // one-time display of backup codes
  DISABLE: 'disable', // code prompt to turn MFA off
  REGENERATE: 'regenerate', // code prompt to reissue backup codes
};

export const useMfaSettings = () => {
  const { data: status, isLoading: isStatusLoading, isError: isStatusError } = useGetMfaStatusQuery();
  const [setupMfa, { isLoading: isSettingUp }] = useSetupMfaMutation();
  const [enableMfa] = useEnableMfaMutation();
  const [disableMfa] = useDisableMfaMutation();
  const [regenerateBackupCodes, { isLoading: isRegenerating }] = useRegenerateBackupCodesMutation();

  const [view, setView] = useState(MFA_VIEW.IDLE);
  const [setupData, setSetupData] = useState(null); // { provisioningUri, qrCode, secret }
  const [backupCodes, setBackupCodes] = useState([]);
  const [actionError, setActionError] = useState(null);
  const [showingRegeneratedCodes, setShowingRegeneratedCodes] = useState(false);
  const suppressModalCancelRef = useRef(false);

  const enabled = !!status?.enabled;
  const requiredByNetwork = !!status?.requiredByNetwork;

  const startSetup = async () => {
    setActionError(null);
    try {
      const data = await setupMfa().unwrap();
      setSetupData(data);
      setView(MFA_VIEW.SETUP);
    } catch (error) {
      setActionError('Could not start two-factor setup. Please try again.');
    }
  };

  const confirmEnable = (code) =>
    enableMfa({ code: code.trim() })
      .unwrap()
      .then((response) => {
        suppressModalCancelRef.current = true;
        trackEvent('authentication:otp:setup_success');
        setBackupCodes(response.backupCodes ?? []);
        setView(MFA_VIEW.BACKUP_CODES);
      });

  const confirmDisable = (code) =>
    disableMfa({ code: code.trim() })
      .unwrap()
      .then(() => {
        trackEvent('user_account:otp:disabled');
        setSetupData(null);
        setBackupCodes([]);
        setView(MFA_VIEW.IDLE);
      });

  // Regenerating now requires re-proving the second factor, so it's a code-entry
  // step (same shape as disable) rather than a one-click action.
  const startRegenerate = () => {
    setActionError(null);
    setView(MFA_VIEW.REGENERATE);
  };

  const confirmRegenerate = (code) =>
    regenerateBackupCodes({ code: code.trim() })
      .unwrap()
      .then((response) => {
        suppressModalCancelRef.current = true;
        setBackupCodes(response.backupCodes ?? []);
        setShowingRegeneratedCodes(true);
        setView(MFA_VIEW.BACKUP_CODES);
      });

  useEffect(() => {
    if (!showingRegeneratedCodes || view !== MFA_VIEW.BACKUP_CODES || !backupCodes.length) {
      return;
    }

    trackEvent('user_account:regenerate_otp_codes');
    setShowingRegeneratedCodes(false);
  }, [showingRegeneratedCodes, view, backupCodes]);

  const finishBackupCodes = () => {
    setShowingRegeneratedCodes(false);
    suppressModalCancelRef.current = false;
    setBackupCodes([]);
    setSetupData(null);
    setView(MFA_VIEW.IDLE);
  };

  const cancel = () => {
    if (suppressModalCancelRef.current) {
      suppressModalCancelRef.current = false;
      return;
    }

    setActionError(null);
    setSetupData(null);
    setView(MFA_VIEW.IDLE);
  };

  return {
    enabled,
    requiredByNetwork,
    isStatusLoading,
    isStatusError,
    isSettingUp,
    isRegenerating,
    view,
    setView,
    setupData,
    backupCodes,
    actionError,
    startSetup,
    confirmEnable,
    confirmDisable,
    startRegenerate,
    confirmRegenerate,
    finishBackupCodes,
    cancel,
  };
};
